import type { ZodType } from 'zod'
import { env } from '@/config/env'
import {
  citizenSafeProse,
  createApiError,
  isApiError,
  kindForStatus,
  messageKeyForKind,
  normalizeThrown,
  type ApiError,
} from '../errors'
import { readBodySafely } from '../readBody'
import { deepUppercase } from '../uppercase'
import { toFormData, type VfsClientFilePart } from './formData'
import {
  emitSessionLost,
  ensureSession,
  getSessionToken,
  hasEstablishedSession,
  setSessionToken,
} from './session'
import { vfsClientAuthFailureSchema, vfsClientEnvelopeSchema } from './vfsClientEnvelope'

/**
 * The transport. Every call the app makes to VFS's backend goes through here.
 *
 * It used to be one of two: `client.ts` carried the journey straight to DHA's gateway
 * on a DHA bearer, and this file carried the audit log and the persisted rows to VFS.
 * Guide §4.8 collapsed that — VFS's backend now calls DHA on our behalf, holding the
 * DHA token itself and logging every call server-side — so the second transport, the
 * second token and the second base URL were all deleted.
 *
 * Two of the old client's behaviours survived the move and are now options rather than
 * defaults, because the routes here need opposite answers:
 *
 *   1. `uppercase` — DHA mandates capitals on submit, so §4.8's routes set it. VFS's
 *      own routes must not: it would uppercase an officer's email.
 *   2. `timeoutMs` — defaults to `env.apiTimeoutMs`, which face verification's 4MB
 *      base64 upload still needs.
 *
 * Two did not survive at all. `X-REQUEST-UUID` was a DHA gateway requirement, and the
 * backend making that call now sets its own. `normaliseResponseKeys` lower-cased the
 * first letter of every key to absorb DHA's PascalCase; the envelope and §4.8 are both
 * documented lowerCamel, so the transform would be a no-op at best and would silently
 * rewrite a round-tripped payload at worst.
 *
 * No credential from the bundle is sent. The backend-issued session token authenticates
 * every call: it goes out in `x-sid-key`, and any re-issued one comes back in
 * `metadata.sessionToken` (ADR 0018). See session.ts for the bootstrap and
 * `vfsClientRequest` for what a 401 and a 403 mean.
 */

/**
 * Mirrors buildUrl in ../url.ts. An empty base keeps every call relative to the page's
 * own origin; an absolute one is called cross-origin, without credentials (ADR 0018).
 */
export function buildVfsClientUrl(
  path: string,
  query?: Record<string, string | undefined>
): string {
  const base = env.vfsClientBaseUrl.replace(/\/$/, '')
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`

  if (!query) return url

  // Dropping undefined rather than serialising it: a missing optional must not become
  // the literal string "undefined" in a query DHA will read.
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.append(key, value)
  }

  const suffix = params.toString()
  return suffix ? `${url}?${suffix}` : url
}

export interface VfsClientRequestOptions<TResponse> {
  path: string
  method?: 'GET' | 'POST' | 'DELETE'
  body?: unknown
  /** Payload schema, applied to the envelope's `data`. Omit when nothing is read back. */
  parse?: ZodType<TResponse>
  signal?: AbortSignal
  /**
   * Reused from the DHA call this row describes, so one value joins our log, DHA's
   * log and the VFS Client row. Generated when the caller has nothing to join to.
   */
  correlationId?: string
  timeoutMs?: number
  /**
   * Survives an unload. Used by the fire-and-forget api-log sender.
   *
   * Must never be combined with `files`: `keepalive` caps a request body at 64 KiB,
   * which every image upload exceeds. Documented rather than enforced because no caller
   * does both — the api-log sender is the only user of either.
   */
  keepalive?: boolean
  /**
   * Appended to the path as a query string, with undefined values dropped.
   *
   * §4.8's lifecycle routes take `?idNo=` alongside the `appRefNo` in their path —
   * DHA does not need it, but the backend's own audit row does. Escaping it here
   * rather than at each call site keeps that correct in one place.
   */
  query?: Record<string, string | undefined>
  /**
   * Uppercase every string leaf of the body before sending it (`deepUppercase`,
   * which exempts `photoImg`/`signatureImg` and the other base64 keys by name).
   *
   * DHA mandates capitals on submit and is still the ultimate consumer of §4.8's
   * bodies, so its routes set this. VFS's own routes must NOT: `auth/token` would
   * uppercase the client secret, and `vac-auth-details` an officer's email.
   */
  uppercase?: boolean
  /**
   * Extra key names whose values survive `uppercase` byte-identical, merged with
   * `deepUppercase`'s own base64 defaults.
   *
   * Ignored unless `uppercase` is set. §4.9's `declarationWording` is the one caller:
   * DHA mandates that text verbatim and §4.17 publishes it in mixed case, so
   * capitalising it is altering it. See declaration.api.ts.
   */
  preserveKeys?: readonly string[]
  /**
   * Binary parts. When present and non-empty the request is sent as
   * `multipart/form-data` and `body` supplies the non-binary fields alongside them.
   *
   * A separate option rather than a Blob inside `body`, and that is load-bearing:
   * `deepUppercase` walks a Blob as a plain object, and `Object.entries(blob)` is empty
   * because every property is on the prototype — so `body: { photoImg: blob }` becomes
   * `{ photoImg: {} }` and uploads nothing, having passed every type and schema check on
   * the way. Keeping binary out of `body` is what makes that unrepresentable.
   */
  files?: readonly VfsClientFilePart[]
  /**
   * Bootstrap a session before this request if the tab has none yet, and recover
   * from a 401 by bootstrapping and retrying once.
   *
   * False for exactly two calls: `POST /api/v1/session` and `POST /api/v1/session/delete`.
   * The bootstrap IS `POST /session` — a session request that asked for one would
   * recurse forever — and both are the only routes that need no session (§2).
   */
  bootstrap?: boolean
}

async function attempt<TResponse = void>(
  options: VfsClientRequestOptions<TResponse>
): Promise<TResponse> {
  const {
    path,
    method = 'POST',
    body,
    parse,
    signal,
    correlationId = crypto.randomUUID(),
    timeoutMs = env.apiTimeoutMs,
    keepalive,
    bootstrap = true,
    query,
    uppercase = false,
    preserveKeys,
    files,
  } = options

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlationId,
  }

  /**
   * A tab with no session token bootstraps first, which saves the 401-and-retry its
   * first request would otherwise start with. Anything after that trusts the token
   * and lets the retry in `vfsClientRequest` handle a lapse.
   */
  if (bootstrap && !hasEstablishedSession()) {
    await ensureSession()
  }

  /**
   * Read after the bootstrap above, so the first request of a page carries the token
   * that bootstrap just adopted. `x-sid-key`, lower-case, with a `Bearer ` prefix: the
   * exact header the shared gateway in front of the API already forwards (ADR 0018).
   * The session request itself presents the current token too, which is what lets the
   * backend renew it with its bindings intact.
   */
  const sessionToken = getSessionToken()
  if (sessionToken !== undefined) headers['x-sid-key'] = `Bearer ${sessionToken}`

  const transformed = body !== undefined && uppercase ? deepUppercase(body, { preserveKeys }) : body

  let payload: string | FormData | undefined
  if (files && files.length > 0) {
    /**
     * No `Content-Type` here, deliberately. Only `fetch` knows the boundary it is about
     * to generate, and a hand-set `multipart/form-data` header omits it — the server
     * then cannot split the parts and answers an opaque 400. Leaving the header off is
     * what lets fetch fill in `multipart/form-data; boundary=...` itself.
     */
    payload = toFormData(transformed, files)
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(transformed)
  }

  const timeout = AbortSignal.timeout(timeoutMs)
  const composedSignal = signal ? AbortSignal.any([signal, timeout]) : timeout

  let response: Response
  try {
    response = await fetch(buildVfsClientUrl(path, query), {
      method,
      headers,
      body: payload,
      signal: composedSignal,
      keepalive,
      /**
       * `omit`, deliberately. The session travels in `x-sid-key`, not a cookie, so
       * there is nothing for the browser to attach — and a credentialed request is
       * held to stricter CORS rules (exact origin plus `Allow-Credentials: true`) that
       * the shared gateway in front of the API does not meet (ADR 0018). Without
       * credentials, the gateway's echoed origin is enough.
       */
      credentials: 'omit',
    })
  } catch (cause) {
    // Interrogate the signals rather than the thrown value — the reasoning
    // client.ts gives: fetch implementations disagree on what an abort throws.
    if (signal?.aborted) {
      throw createApiError({
        kind: 'aborted',
        messageKey: messageKeyForKind('aborted'),
        correlationId,
        cause,
      })
    }
    if (timeout.aborted) {
      throw createApiError({
        kind: 'timeout',
        messageKey: messageKeyForKind('timeout'),
        correlationId,
        cause,
      })
    }
    throw normalizeThrown(cause, correlationId)
  }

  const raw = await readBodySafely(response)

  if (!response.ok) {
    throw toVfsClientError(response.status, raw, correlationId)
  }

  const envelope = vfsClientEnvelopeSchema.safeParse(raw)

  /**
   * A 2xx that is not an envelope is a proxy or a login page answering in our
   * place — the failure mode that produces "Unexpected token '<'". Treated as a
   * parse error rather than as a silent success.
   */
  if (!envelope.success) {
    throw createApiError({
      kind: 'parse',
      status: response.status,
      messageKey: messageKeyForKind('parse'),
      detail: 'Response was not a VFS Client envelope',
      correlationId,
      cause: envelope.error,
    })
  }

  // A 2xx carrying `success: false` is not something the guide documents, but an
  // envelope that says it failed is the more specific of the two signals.
  if (!envelope.data.success) {
    throw toVfsClientError(response.status, raw, correlationId)
  }

  // The session was (re-)issued during this request: adopt it before anything else
  // runs, so the next request presents the escalated binding rather than the old one.
  const issuedToken = envelope.data.metadata?.sessionToken
  if (issuedToken) setSessionToken(issuedToken)

  if (!parse) return undefined as TResponse

  const result = parse.safeParse(envelope.data.data)
  if (result.success) return result.data

  throw createApiError({
    kind: 'parse',
    status: response.status,
    messageKey: messageKeyForKind('parse'),
    detail: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    correlationId,
    cause: result.error,
  })
}

/**
 * What a 401 and a 403 mean, and the one retry.
 *
 * A 401 is always the auth middleware — no controller ran, nothing was written — so
 * replaying even a POST is safe, and the cure is a fresh bootstrap: `POST /session`
 * re-issues a cookie the backend still recognises, or mints an anonymous one when it
 * does not. The retry calls `attempt`, not `vfsClientRequest`, so there is no path
 * back into this catch: "do not loop" holds structurally rather than by a counter.
 *
 * If the retry is refused too, or if any answer is a 403 `FORBIDDEN`, the session
 * has genuinely gone — expired past recovery, or re-bound to another application
 * by a second tab (dev plan §3.2) — and no request from this tab can succeed. Both
 * become `session_expired`: the expiry watcher is told (`emitSessionLost`) so the
 * wizard restarts with an explanation, and the caller gets an error it can render
 * in the moment before that happens.
 *
 * `OFFICER_REQUIRED` and `CSRF_REJECTED` are 403s too, and are deliberately NOT
 * this: the first is step ordering (sign an officer in), the second a request the
 * same-site guard refused. Both stay ordinary `forbidden` errors, code attached.
 *
 * Skipped entirely for `bootstrap: false`, which is the session request itself: a
 * 401 there cannot happen, and a retry would recurse. Mutations otherwise never
 * retry — nothing is idempotent (docs/api-gaps.md #8).
 */
export async function vfsClientRequest<TResponse = void>(
  options: VfsClientRequestOptions<TResponse>
): Promise<TResponse> {
  try {
    return await attempt<TResponse>(options)
  } catch (error) {
    if (options.bootstrap === false || !isApiError(error)) throw error
    if (isSessionLost(error)) throw lostSession(error)
    if (error.kind !== 'unauthorized') throw error

    if (!env.isProduction) console.warn('[vfsclient] 401 — bootstrapping again and retrying once.')
    await ensureSession()

    try {
      return await attempt<TResponse>(options)
    } catch (retryError) {
      if (
        isApiError(retryError) &&
        (retryError.kind === 'unauthorized' || isSessionLost(retryError))
      ) {
        throw lostSession(retryError)
      }
      throw retryError
    }
  }
}

/** A 403 whose code says the session does not own what it asked for. */
function isSessionLost(error: ApiError): boolean {
  return error.kind === 'forbidden' && error.errorCode === 'FORBIDDEN'
}

function lostSession(error: ApiError): ApiError {
  emitSessionLost()
  return createApiError({
    kind: 'session_expired',
    status: error.status,
    messageKey: messageKeyForKind('session_expired'),
    detail: error.detail,
    errorCode: error.errorCode,
    correlationId: error.correlationId,
    cause: error,
  })
}

/**
 * Turns either failure shape into an ApiError.
 *
 * `errorCode` is carried into `detail` rather than into a new field: it is a short
 * machine string (`VALIDATION_ERROR`, `NOT_FOUND`) that belongs in diagnostics, and
 * the citizen-facing text still comes from `messageKey` via the i18n catalogue.
 */
function toVfsClientError(status: number, body: unknown, correlationId: string): ApiError {
  const kind = kindForStatus(status)

  // A rejection that never reached a controller, so it carries no envelope — IIS or
  // a gateway answering in the backend's place. The JWT middleware's own 401 IS
  // enveloped, so this is a fallback rather than the expected shape.
  const authFailure = vfsClientAuthFailureSchema.safeParse(body)
  const envelope = vfsClientEnvelopeSchema.safeParse(body)

  if (!envelope.success) {
    /**
     * No `correlationId`, deliberately. The id we sent is only worth quoting when
     * the backend recorded it, and a response without an envelope is the proof
     * that it did not: nothing of ours ran, so the api-log has no row to find. A
     * "Reference: 4c06114a" beside an IIS error page sends support looking for a
     * trail that does not exist. The id still went out in `X-Correlation-Id`, so
     * the browser's network tab has it for anyone debugging in the moment.
     */
    return createApiError({
      kind,
      status,
      messageKey: messageKeyForKind(kind),
      detail: authFailure.success ? authFailure.data.message : undefined,
    })
  }

  const { message, errorCode, errors } = envelope.data
  const parts = [
    errorCode ? `[${errorCode}]` : undefined,
    message ?? undefined,
    errors?.length ? errors.join('; ') : undefined,
  ].filter(Boolean)

  return createApiError({
    kind,
    status,
    messageKey: messageKeyForKind(kind),
    detail: parts.length > 0 ? parts.join(' ').slice(0, 500) : undefined,
    errorCode: errorCode ?? undefined,
    /**
     * §4.8 proxies DHA, and when DHA refuses, the backend answers 502 with
     * `errorCode: DHA_API_ERROR` and DHA's own sentence in `message` — "Applicant is
     * not eligible for this product", not "Bad gateway". `kindForStatus` maps 502 to
     * `server`, whose catalogue line says the service is unavailable: both wrong and
     * unactionable. `useApiErrorMessage` renders `reason` in preference to the
     * catalogue line, so setting it here is the whole fix — no page changes.
     *
     * Keyed on the errorCode rather than the route, so a VALIDATION_ERROR raised by
     * VFS's own controller keeps its catalogue line. That distinction matters: those
     * messages are written for an integrator, not for a citizen.
     *
     * `kind` deliberately stays `server`. Remapping it would lie about the transport
     * and would flip `retryable` to false — right for a business rejection, wrong for
     * a genuine DHA outage arriving under the same code.
     *
     * `OFFICER_REQUIRED` gets the same treatment: the backend's sentence ("an officer
     * must be recorded against this application…") is the actionable one, where the
     * catalogue's `forbidden` line — "you do not have permission" — is not.
     */
    reason:
      errorCode === 'DHA_API_ERROR' || errorCode === 'OFFICER_REQUIRED'
        ? citizenSafeProse(message)
        : undefined,
    correlationId: envelope.data.correlationId ?? correlationId,
  })
}
